import { MathHelper } from '@core/helpers/math.helper';
import { BudgetTypeEnum } from '@enums/BudgetType';
import { StatusEnum } from '@enums/Status';
import { StructureDepthEnum } from '@enums/StructureDepth';
import { AverageRateDto } from '@interfaces/AverageRateDto';
import { ClassificationLine } from '@interfaces/ClassificationLine';
import { CompanyCurrency } from '@interfaces/CompanyCurrency';
import { Estimate } from '@interfaces/Estimate';
import { EstimateLine } from '@interfaces/EstimateLine';
import { ExpenseBillingMethodDto } from '@interfaces/ExpenseBillingMethodDto';
import { InternalFeeBillingMethodDto } from '@interfaces/InternalFeeBillingMethodDto';
import { InternalFeeLine } from '@interfaces/InternalFeeLine';
import { LaborBillingMethodDto } from '@interfaces/LaborBillingMethodDto';
import { BillingMethodService } from '@shared/services/billing-method.service';
import { CurrencyService } from '@shared/services/currency.service';
import { ProjectTotals } from '@shared/services/models/project-totals.model';

export class TreeNodeTotals implements ProjectTotals {
  // Time
  public totalHours = 0;
  public totalHoursCost = 0;
  public totalHoursBillable = 0;
  public totalHoursBillableCurrency = 0;
  public totalHoursBillableFinal = 0; // will always be in the project's currency (aka not always functional)
  public totalHoursBillableFinalFunctionalCurrency = 0;
  public classificationTotals = this.iniSubTotals(this.currentEstimate.classificationEstimates ?? []);

  // Internal Fees
  public totalInternalFeesQuantity = 0;
  public totalInternalFeesCost = 0;
  public totalInternalFeesBillable = 0;
  public totalInternalFeesBillableCurrency = 0;
  public totalInternalFeesBillableFinal = 0; // will always be in the project's currency (aka not always functional)
  public totalInternalFeesBillableFinalFunctionalCurrency = 0;
  public internalResourceTotals = this.iniSubTotals(this.currentEstimate.internalResourceEstimates ?? []);

  // Expense
  public expenseCostQuantity?: number;
  public expenseCostUnitCost?: number;
  public totalExpensesCost?: number;
  public totalExpensesBillable = 0;
  public totalExpensesBillableCurrency = 0;
  public totalExpensesBillableFinal?: number; // will always be in the project's currency (aka not always functional)
  public totalExpensesBillableFinalFunctionalCurrency = 0;
  public expenseMethodUnitCost?: number = undefined; // used to determine if the unitCost should be readonly (from the billing method)

  // Totals
  public totalCost = 0;
  public totalBillable = 0;
  public totalBillableCurrency = 0;
  public totalBillableFinal = 0;
  public totalBillableFinalCurrency = 0;

  public rate = 0;
  public availableHoursPercent = 0;

  public constructor(
    private currentEstimate: Estimate,
    private billingMethods: {
      labor: LaborBillingMethodDto[];
      internalFees: InternalFeeBillingMethodDto[];
      expense: ExpenseBillingMethodDto[];
    },
    private classDepartmentRates: AverageRateDto[] = [],
    public departmentCurrency?: CompanyCurrency
  ) {}

  private sumOfLabor(childTotals: TreeNodeTotals) {
    for (const classificationTotal in childTotals.classificationTotals) {
      if (this.classificationTotals.hasOwnProperty(classificationTotal)) {
        this.classificationTotals[classificationTotal] += childTotals.classificationTotals[classificationTotal];
      }
    }
    this.totalHours += childTotals.totalHours;
    this.totalHoursCost += childTotals.totalHoursCost;
    this.totalHoursBillable += childTotals.totalHoursBillable;
    this.totalHoursBillableCurrency += childTotals.totalHoursBillableCurrency;
    this.totalHoursBillableFinal += childTotals.totalHoursBillableFinal;
    this.totalHoursBillableFinalFunctionalCurrency += childTotals.totalHoursBillableFinalFunctionalCurrency;
  }

  private sumOfInternalFees(childTotals: TreeNodeTotals) {
    for (const internalResourceTotal in childTotals.internalResourceTotals) {
      if (this.internalResourceTotals.hasOwnProperty(internalResourceTotal)) {
        this.internalResourceTotals[internalResourceTotal] += childTotals.internalResourceTotals[internalResourceTotal];
      }
    }
    this.totalInternalFeesQuantity += childTotals.totalInternalFeesQuantity;
    this.totalInternalFeesCost += childTotals.totalInternalFeesCost;
    this.totalInternalFeesBillable += childTotals.totalInternalFeesBillable;
    this.totalInternalFeesBillableCurrency += childTotals.totalInternalFeesBillableCurrency;
    this.totalInternalFeesBillableFinal += childTotals.totalInternalFeesBillableFinal;
    this.totalInternalFeesBillableFinalFunctionalCurrency +=
      childTotals.totalInternalFeesBillableFinalFunctionalCurrency;
  }

  private sumOfExpenses(childTotals: TreeNodeTotals) {
    this.expenseCostQuantity = undefined;
    this.expenseCostUnitCost = undefined;
    this.totalExpensesCost = (this.totalExpensesCost ?? 0) + (childTotals.totalExpensesCost ?? 0);
    this.totalExpensesBillable = this.totalExpensesBillable + childTotals.totalExpensesBillable;
    this.totalExpensesBillableCurrency = this.totalExpensesBillableCurrency + childTotals.totalExpensesBillableCurrency;
    this.totalExpensesBillableFinal =
      (this.totalExpensesBillableFinal ?? 0) + (childTotals.totalExpensesBillableFinal ?? 0);
    this.totalExpensesBillableFinalFunctionalCurrency += childTotals.totalExpensesBillableFinalFunctionalCurrency;
  }

  private sumOfTotals(childTotals: TreeNodeTotals) {
    this.totalCost += childTotals.totalCost;
    this.totalBillable += childTotals.totalBillable;
    this.totalBillableCurrency += childTotals.totalBillableCurrency;
    this.totalBillableFinal += childTotals.totalBillableFinal;
    this.totalBillableFinalCurrency += childTotals.totalBillableFinalCurrency;
  }

  private roundTotals() {
    this.totalHours = MathHelper.round2Decimals(this.totalHours);
    this.totalHoursCost = MathHelper.round2Decimals(this.totalHoursCost);
    this.totalHoursBillable = MathHelper.round2Decimals(this.totalHoursBillable);
    this.totalExpensesCost = MathHelper.round2Decimals(this.totalExpensesCost);
    this.totalExpensesBillable = MathHelper.round2Decimals(this.totalExpensesBillable);
    this.totalBillableFinal = MathHelper.round2Decimals(this.totalBillableFinal);
  }

  public addToTotals(childTotals: TreeNodeTotals) {
    this.sumOfLabor(childTotals);
    this.sumOfInternalFees(childTotals);
    this.sumOfExpenses(childTotals);
    this.sumOfTotals(childTotals);
    this.roundTotals();
  }

  public setTotals(
    currentEstimate: Estimate,
    estimateLine: EstimateLine,
    functionalCurrencyCode: string,
    hasPC: boolean,
    isGenericTimeNode = false,
    isGenericInternalFeeNode = false,
    isGenericExpenseNode = false
  ) {
    this.totalHours = 0;
    this.totalHoursCost = 0;
    this.totalInternalFeesQuantity = 0;
    this.totalInternalFeesCost = 0;

    const isFunctionalCurrency =
      currentEstimate.currencyCode === null || currentEstimate.currencyCode === functionalCurrencyCode;

    // Time
    if (estimateLine.classificationLines && !isGenericExpenseNode && !isGenericInternalFeeNode) {
      let anyClassLineHasHours = false;

      for (const classificationLine of estimateLine.classificationLines) {
        if (currentEstimate.status === StatusEnum.New) {
          classificationLine.rateCost = this.getClassificationCostRate(
            currentEstimate,
            estimateLine,
            classificationLine
          );
          classificationLine.rateBillable = this.getClassificationBillableRate(
            currentEstimate,
            estimateLine,
            classificationLine,
            classificationLine.rateCost,
            isGenericTimeNode,
            isGenericInternalFeeNode,
            isGenericExpenseNode
          );
        }
        anyClassLineHasHours = anyClassLineHasHours || classificationLine.hours !== 0;
        this.classificationTotals[classificationLine.classificationEstimateId] = classificationLine.hours;
        this.totalHours += classificationLine.hours;
        this.totalHoursCost += classificationLine.hours * classificationLine.rateCost;
        this.totalHoursBillable += classificationLine.hours * classificationLine.rateBillable;
      }

      if (hasPC && !anyClassLineHasHours) {
        estimateLine.laborBillableFinal = null;
      }

      if (isFunctionalCurrency) {
        this.totalHoursBillableFinal = estimateLine.laborBillableFinal ?? this.totalHoursBillable;
      } else {
        this.totalHoursBillableCurrency = MathHelper.round2Decimals(
          CurrencyService.applyMultiplyer(
            this.totalHoursBillable,
            currentEstimate.currencyRateOperation,
            currentEstimate.currencyRate
          )
        );
        this.totalHoursBillableFinal = estimateLine.laborBillableFinal ?? this.totalHoursBillableCurrency;
        this.totalHoursBillableFinalFunctionalCurrency = MathHelper.round2Decimals(
          CurrencyService.removeMultiplyer(
            this.totalHoursBillableFinal,
            currentEstimate.currencyRateOperation,
            currentEstimate.currencyRate
          )
        );
      }

      this.totalHours = MathHelper.round2Decimals(this.totalHours);
      this.totalHoursCost = MathHelper.round2Decimals(this.totalHoursCost);
      this.totalHoursBillable = MathHelper.round2Decimals(this.totalHoursBillable);
      this.totalHoursBillableFinal = MathHelper.round2Decimals(this.totalHoursBillableFinal);
    }

    // Internal Fees
    if (estimateLine.internalFeeLines && !isGenericExpenseNode && !isGenericTimeNode) {
      for (const internalFeeLine of estimateLine.internalFeeLines) {
        if (currentEstimate.status == StatusEnum.New) {
          const { internalFeeBillableRate, internalFeeCostRate } = this.getInternalFeeRates(
            currentEstimate,
            estimateLine,
            internalFeeLine
          );
          internalFeeLine.rateBillable = internalFeeBillableRate;
          internalFeeLine.rateCost = internalFeeCostRate;
        }

        this.internalResourceTotals[internalFeeLine.internalResourceEstimateId] = internalFeeLine.quantity;
        this.totalInternalFeesQuantity += internalFeeLine.quantity;
        this.totalInternalFeesCost += internalFeeLine.quantity * internalFeeLine.rateCost;
        this.totalInternalFeesBillable += internalFeeLine.quantity * internalFeeLine.rateBillable;
      }

      if (isFunctionalCurrency) {
        this.totalInternalFeesBillableFinal = estimateLine.internalFeeBillableFinal ?? this.totalInternalFeesBillable;
      } else {
        this.totalInternalFeesBillableCurrency = MathHelper.round2Decimals(
          CurrencyService.applyMultiplyer(
            this.totalInternalFeesBillable,
            currentEstimate.currencyRateOperation,
            currentEstimate.currencyRate
          )
        );
        this.totalInternalFeesBillableFinal =
          estimateLine.internalFeeBillableFinal ?? this.totalInternalFeesBillableCurrency;
        this.totalInternalFeesBillableFinalFunctionalCurrency = MathHelper.round2Decimals(
          CurrencyService.removeMultiplyer(
            this.totalInternalFeesBillableFinal,
            currentEstimate.currencyRateOperation,
            currentEstimate.currencyRate
          )
        );
      }

      this.totalInternalFeesQuantity = MathHelper.round2Decimals(this.totalInternalFeesQuantity);
      this.totalInternalFeesCost = MathHelper.round2Decimals(this.totalInternalFeesCost);
      this.totalInternalFeesBillable = MathHelper.round2Decimals(this.totalInternalFeesBillable);
      this.totalInternalFeesBillableFinal = MathHelper.round2Decimals(this.totalInternalFeesBillableFinal);
    }

    // Expense
    if (!isGenericTimeNode && !isGenericInternalFeeNode) {
      const { expenseBillableRate, expenseCostRate, expenseBillingMethod } = this.getExpenseRates(
        currentEstimate,
        estimateLine
      );
      // if the billing method has a unitCost, only the quantity is from user, other fields are calculated (for a new estimation)
      if (expenseBillingMethod?.expenseType === 1) {
        this.expenseMethodUnitCost = expenseCostRate;
        if (estimateLine.expenseLine && currentEstimate.status === StatusEnum.New) {
          estimateLine.expenseLine.unitCost = 0;
          estimateLine.expenseLine.amountCost = MathHelper.round2Decimals(
            (estimateLine.expenseLine.quantity ?? 0) * expenseCostRate
          );
        }
      } else {
        this.expenseMethodUnitCost = undefined;
      }

      if (estimateLine.expenseLine && currentEstimate.status === StatusEnum.New) {
        estimateLine.expenseLine.amountBillable = MathHelper.round2Decimals(
          (estimateLine.expenseLine.quantity ?? 0) * expenseBillableRate
        );
      }

      this.expenseCostQuantity = estimateLine.expenseLine?.quantity;
      this.expenseCostUnitCost = this.expenseMethodUnitCost ?? estimateLine.expenseLine?.unitCost;
      this.totalExpensesCost = estimateLine.expenseLine?.amountCost;
      this.totalExpensesBillable = estimateLine.expenseLine?.amountBillable ?? 0;

      if (estimateLine.expenseLine && hasPC && !this.totalExpensesCost) {
        estimateLine.expenseLine.billableFinal = null;
      }

      if (isFunctionalCurrency) {
        this.totalExpensesBillableFinal = estimateLine.expenseLine?.billableFinal ?? this.totalExpensesBillable;
      } else {
        this.totalExpensesBillableCurrency = MathHelper.round2Decimals(
          CurrencyService.applyMultiplyer(
            estimateLine.expenseLine?.amountBillable ?? 0,
            currentEstimate.currencyRateOperation,
            currentEstimate.currencyRate
          )
        );
        this.totalExpensesBillableFinal = estimateLine.expenseLine?.billableFinal ?? this.totalExpensesBillableCurrency;
        this.totalExpensesBillableFinalFunctionalCurrency = MathHelper.round2Decimals(
          CurrencyService.removeMultiplyer(
            this.totalExpensesBillableFinal,
            currentEstimate.currencyRateOperation,
            currentEstimate.currencyRate
          )
        );
      }

      this.totalExpensesBillableFinal = MathHelper.round2Decimals(this.totalExpensesBillableFinal);
    }

    // Totals
    this.totalCost = this.totalHoursCost + (this.totalExpensesCost ?? 0) + this.totalInternalFeesCost;
    this.totalBillable = this.totalHoursBillable + this.totalExpensesBillable + this.totalInternalFeesBillable;
    this.totalBillableFinal =
      this.totalHoursBillableFinal + (this.totalExpensesBillableFinal ?? 0) + this.totalInternalFeesBillableFinal;
    this.totalBillableFinal = MathHelper.round2Decimals(this.totalBillableFinal);

    if (!isFunctionalCurrency) {
      this.totalBillableCurrency =
        this.totalHoursBillableCurrency + this.totalExpensesBillableCurrency + this.totalInternalFeesBillableCurrency;
      this.totalBillableFinalCurrency =
        this.totalHoursBillableFinal + this.totalExpensesBillableFinal + this.totalInternalFeesBillableFinal;
    }
  }

  public updateTotalExpensesCost() {
    this.totalExpensesCost = MathHelper.round2Decimals(
      (this.expenseCostUnitCost ?? 0) * (this.expenseCostQuantity ?? 0)
    );
  }

  public setTotalExpenseCost(totalExpensesCost: number) {
    this.expenseCostQuantity = 1; // Always set the qnt to 1 to avoid calculation errors due to precision
    this.expenseCostUnitCost = MathHelper.round2Decimals(totalExpensesCost);
  }

  private iniSubTotals<T extends { id: string }[]>(estimates: T): Record<string, number> {
    return estimates.reduce((totals, { id }) => ({ ...totals, [id]: null }), {});
  }

  private getClassificationCostRate(
    currentEstimate: Estimate,
    estimateLine: EstimateLine,
    classificationLine: ClassificationLine
  ): number {
    if (currentEstimate.status === StatusEnum.New) {
      // if new use rates table
      const classCode = classificationLine.classificationEstimate.classification.code;
      const deptCode = estimateLine.departmentEstimate.department.code;
      const departmentRate = this.classDepartmentRates.find(
        r => r.classificationCode.toUpperCase() === classCode.toUpperCase() && r.departmentCode === deptCode
      );

      return departmentRate?.rate ?? 0;
    }

    return classificationLine?.rateCost ?? 0;
  }

  private getClassificationBillableRate(
    currentEstimate: Estimate,
    estimateLine: EstimateLine,
    classificationLine: ClassificationLine,
    classCostRate: number,
    isGenericTimeNode: boolean,
    isGenericInternalFeeNode: boolean,
    isGenericExpenseNode: boolean
  ): number {
    const laborBillingMethodCode = currentEstimate.laborBillingMethodCode;
    let isUsingLaborBillingMethod = false;

    if (currentEstimate.structureDepth === StructureDepthEnum.Level2) {
      isUsingLaborBillingMethod = estimateLine.structure.level2IsUsingLaborBillingMethod;
    } else {
      isUsingLaborBillingMethod = estimateLine.structure.level1IsUsingLaborBillingMethod;
    }

    let rateBillable = 0;

    if (isUsingLaborBillingMethod && laborBillingMethodCode) {
      const startingDate = this.getStartDate(estimateLine);

      if (startingDate) {
        const isGenericNode =
          currentEstimate.budgetType === BudgetTypeEnum.Summary ||
          isGenericTimeNode ||
          isGenericInternalFeeNode ||
          isGenericExpenseNode;

        const laborBillingMethod = BillingMethodService.getLaborBillingMethod(
          this.billingMethods.labor,
          classificationLine.classificationEstimate.classification.code,
          startingDate,
          isGenericNode || !estimateLine.category ? '' : estimateLine.category.code
        );

        rateBillable = BillingMethodService.getLaborBillableRate(
          classCostRate,
          currentEstimate.rate,
          laborBillingMethod
        );
      }
    } else {
      rateBillable = MathHelper.round2Decimals(classCostRate * currentEstimate.rate);
    }

    return rateBillable;
  }

  private getInternalFeeRates(currentEstimate: Estimate, estimateLine: EstimateLine, internalFeeLine: InternalFeeLine) {
    const startingDate = this.getStartDate(estimateLine);
    const internalFeeCode = currentEstimate.internalFeeBillingMethodCode;

    const internalFeeBillingMethod = BillingMethodService.getInternalFeeBillingMethod(
      this.billingMethods.internalFees,
      internalFeeCode,
      startingDate,
      estimateLine.category?.code,
      internalFeeLine.internalResourceEstimate.internalResource.code
    );

    const internalFeeCostRate = BillingMethodService.getInternalFeeCostRate(internalFeeLine, internalFeeBillingMethod);
    const internalFeeBillableRate = BillingMethodService.getInternalFeeBillableRate(
      internalFeeCostRate,
      internalFeeBillingMethod
    );

    return { internalFeeCostRate, internalFeeBillableRate };
  }

  private getExpenseRates(currentEstimate: Estimate, estimateLine: EstimateLine) {
    const startingDate = this.getStartDate(estimateLine);
    const expenseCode = currentEstimate.expenseBillingMethodCode;

    const expenseBillingMethod = BillingMethodService.getExpenseBillingMethod(
      this.billingMethods.expense,
      expenseCode,
      startingDate,
      estimateLine.category?.code
    );

    const expenseCostRate = BillingMethodService.getExpenseCostRate(estimateLine.expenseLine, expenseBillingMethod);
    const expenseBillableRate = BillingMethodService.getExpenseBillableRate(expenseCostRate, expenseBillingMethod);

    return { expenseBillingMethod, expenseCostRate, expenseBillableRate };
  }

  private getStartDate(estimateLine: EstimateLine) {
    if (estimateLine.categoryDetail) {
      if (estimateLine.categoryDetail.dateStart) {
        return estimateLine.categoryDetail.dateStart;
      }
    }
    if (estimateLine.departmentDetail) {
      if (estimateLine.departmentDetail.dateStart) {
        return estimateLine.departmentDetail.dateStart;
      }
    }
    if (estimateLine.structure.level1Detail) {
      if (estimateLine.structure.level1Detail.dateStart) {
        return estimateLine.structure.level1Detail.dateStart;
      }
    }
    if (estimateLine.structure.level2Detail) {
      if (estimateLine.structure.level2Detail.dateStart) {
        return estimateLine.structure.level2Detail.dateStart;
      }
    }
    if (estimateLine.structure.estimate.dateStart) {
      return estimateLine.structure.estimate.dateStart;
    }

    return null;
  }
}
